import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image, Spin, Empty } from 'antd';
import { appMessage as message } from '../../utils/appMessage';
import { CameraOutlined, DeleteOutlined } from '@ant-design/icons';

import { AppButton } from '../common/AppButton';
import { AppPopconfirm } from '../common/AppPopconfirm';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { FONT_SIZE, FONT_WEIGHT, SPACING, RADIUS } from '../../constants/constants';
import {
  fetchBuybackPhotos, uploadBuybackPhoto, fetchBuybackPhotoBlob, deleteBuybackPhoto,
} from '../../api/api';
import type { BuybackPhoto } from '../../types/buyback';

interface Props {
  buybackId: number;
  /** true 면 첨부·삭제 없이 보기 전용(완료/불성립 내역 확인용). */
  readOnly?: boolean;
}

const THUMB = 96;
// 업로드 전 클라이언트에서 축소할 최대 변(px)과 JPEG 품질 — DB 용량·전송량을 줄인다.
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

/**
 * 지정한 변을 넘지 않게 캔버스로 축소해 JPEG Blob 으로 변환한다. 원본이 이미 작으면
 * 그대로(품질만 재인코딩) 반환한다. 변환 실패 시 원본 파일을 그대로 올린다.
 */
async function downscaleToJpeg(file: File): Promise<{ blob: Blob; name: string }> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY));
    if (!blob) throw new Error('toBlob failed');
    const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
    return { blob, name: `${base}.jpg` };
  } catch {
    return { blob: file, name: file.name };
  }
}

/**
 * 매입 오더 실물 카드 사진(오더 단위). 직원이 창구에서 카드를 촬영해 첨부한다 —
 * "혹시 모를" 기록용이라 강제하지 않는다. 인증이 필요한 스트리밍이라 각 사진은 blob 으로
 * 받아 objectURL 로 표시한다.
 */
export const BuybackPhotos: React.FC<Props> = ({ buybackId, readOnly }) => {
  const t = useThemeTokens();
  const [photos, setPhotos]     = useState<BuybackPhoto[]>([]);
  const [urls, setUrls]         = useState<Record<number, string>>({});
  const [loading, setLoading]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // 언마운트·재로딩 시 revoke 할 objectURL 들을 모아 둔다.
  const urlsRef = useRef<string[]>([]);

  const revokeAll = () => { urlsRef.current.forEach(URL.revokeObjectURL); urlsRef.current = []; };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchBuybackPhotos(buybackId);
      const entries = await Promise.all(list.map(async (p) => {
        try {
          const blob = await fetchBuybackPhotoBlob(buybackId, p.id);
          const url = URL.createObjectURL(blob);
          urlsRef.current.push(url);
          return [p.id, url] as const;
        } catch { return [p.id, ''] as const; }
      }));
      setPhotos(list);
      setUrls(Object.fromEntries(entries));
    } catch {
      message.error('사진을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [buybackId]);

  useEffect(() => {
    load();
    return revokeAll; // 오더가 바뀌거나 닫힐 때 이전 objectURL 정리
  }, [load]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // 같은 파일 재선택 허용
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const { blob, name } = await downscaleToJpeg(f);
        await uploadBuybackPhoto(buybackId, blob, name);
      }
      revokeAll();
      await load();
      message.success(files.length === 1 ? '사진을 첨부했습니다.' : `사진 ${files.length}장을 첨부했습니다.`);
    } catch {
      message.error('사진 첨부 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (photoId: number) => {
    try {
      await deleteBuybackPhoto(buybackId, photoId);
      revokeAll();
      await load();
    } catch {
      message.error('사진 삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <div style={{ marginBottom: SPACING.sm }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, marginBottom: SPACING.xs }}>
        <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
          실물 사진
        </span>
        <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>
          {photos.length > 0 ? `${photos.length}장` : '첨부 없음'}{!readOnly && ' · 선택 사항'}
        </span>
        {!readOnly && (
          <AppButton
            size="sm" icon={<CameraOutlined />} loading={uploading}
            onClick={() => inputRef.current?.click()}
            style={{ marginLeft: 'auto' }}
          >
            사진 촬영·첨부
          </AppButton>
        )}
        {/* capture=environment: 태블릿·폰에서는 후면 카메라가 바로 열린다. PC는 파일 선택. */}
        <input
          ref={inputRef} type="file" accept="image/*" capture="environment" multiple
          style={{ display: 'none' }} onChange={onPick}
        />
      </div>

      {loading ? (
        <div style={{ padding: SPACING.md, textAlign: 'center' }}><Spin size="small" /></div>
      ) : photos.length === 0 ? (
        readOnly
          ? <Empty description="첨부된 사진이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          : null
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.xs }}>
          <Image.PreviewGroup>
            {photos.map((p) => (
              <div key={p.id} style={{ position: 'relative', width: THUMB, height: THUMB }}>
                <Image
                  src={urls[p.id]}
                  alt={p.originalName ?? '실물 사진'}
                  width={THUMB} height={THUMB}
                  style={{ objectFit: 'cover', borderRadius: RADIUS.md, border: `1px solid ${t.border}` }}
                />
                {!readOnly && (
                  <AppPopconfirm title="이 사진을 삭제하시겠습니까?" onConfirm={() => remove(p.id)}>
                    <div
                      role="button"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute', top: 2, right: 2,
                        background: 'rgba(0,0,0,0.55)', color: '#fff',
                        borderRadius: RADIUS.sm, width: 22, height: 22,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', fontSize: FONT_SIZE.xs,
                      }}
                    >
                      <DeleteOutlined />
                    </div>
                  </AppPopconfirm>
                )}
              </div>
            ))}
          </Image.PreviewGroup>
        </div>
      )}
    </div>
  );
};
